import { ARCHETYPE_BY_ID, AURA_FX, COSMETIC_BY_ID, ITEM_FIT, SLOT_ORDER, SLOT_SOCKET, type AuraFx, type HandSocket, type Socket, type WearMotion } from "@nano/content";
import type { AvatarId, AvatarPose, Equipment, EquipmentSlot } from "@nano/contracts";

export interface Size {
  readonly width: number;
  readonly height: number;
}

/** Fractions of a sprite: the part that is drawn. */
export interface Crop {
  readonly x0: number;
  readonly y0: number;
  readonly x1: number;
  readonly y1: number;
}

export interface WearPiece {
  readonly slot: EquipmentSlot;
  readonly itemId: string;
  readonly assetId: string;
  /** Position of the seat point, relative to the body's top-left. */
  readonly x: number;
  readonly y: number;
  /** Display width/height in the same units as the body. */
  readonly width: number;
  readonly height: number;
  readonly originX: number;
  readonly originY: number;
  /** Negative when mirrored (a prop in the left hand). */
  readonly scaleX: number;
  /** Radians, clockwise. */
  readonly rotation: number;
  readonly layer: "back" | "front";
  readonly crop?: Crop | undefined;
  readonly motion?: WearMotion | undefined;
}

/** A thin bar (glasses temple in profile), in body units. */
export interface WearArm {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly thickness: number;
  readonly color: number;
}

export interface WearAura {
  readonly itemId: string;
  readonly assetId: string;
  readonly fx: AuraFx;
  /** Centre of the effect and the body height it scales with. */
  readonly x: number;
  readonly y: number;
  readonly size: number;
}

export interface WearLayout {
  readonly pieces: WearPiece[];
  readonly arms: WearArm[];
  /** Hand outlines (fractions of the body sprite) drawn again over a held prop, so it sits in the fist. */
  readonly hands: (readonly (readonly [number, number])[])[];
  readonly aura: WearAura | null;
}

const DEG = Math.PI / 180;

/** Default edge-on squash for flat art in the side pose. */
const SIDE_SQUASH: Partial<Record<EquipmentSlot, number>> = {
  face: 0.72,
  neck: 0.6,
  back: 0.55,
  hair_accessory: 0.8,
};

/**
 * Pure accessory layout shared by the Phaser avatar and the DOM preview, so
 * a crown sits on the same spot in the shop and in the city. Every item is
 * fitted to a measured body socket (see `archetypes.ts` and `wear.ts`).
 */
export function layoutWear(
  avatarId: AvatarId,
  pose: AvatarPose,
  equipped: Equipment,
  body: Size,
  itemSize: (assetId: string) => Size | undefined,
): WearLayout {
  const layout: { -readonly [K in keyof WearLayout]: WearLayout[K] } = { pieces: [], arms: [], hands: [], aura: null };
  const archetype = ARCHETYPE_BY_ID.get(avatarId);
  if (!archetype) return layout;
  const sockets = archetype.sockets[pose];
  const H = body.height;
  for (const slot of SLOT_ORDER) {
    const itemId = equipped[slot];
    if (!itemId) continue;
    const item = COSMETIC_BY_ID.get(itemId);
    if (!item || item.slot !== slot || item.hiddenInPoses.includes(pose)) continue;
    const assetId = item.assetIds[pose] ?? item.assetIds.front;

    if (slot === "aura") {
      const fx = AURA_FX[itemId];
      if (fx) layout.aura = { itemId, assetId, fx, x: sockets.aura.x * body.width, y: sockets.aura.y * H, size: H };
      continue;
    }

    const socket: (Socket & Partial<HandSocket>) | undefined = sockets[SLOT_SOCKET[slot]];
    const fit = ITEM_FIT[itemId];
    const natural = itemSize(assetId);
    if (!socket || !fit || !natural) continue;

    const tweak = item.anchorsByPose[pose];
    // Items with their own art for this pose (a hat in profile) use that art's fit.
    const view = item.assetIds[pose] ? fit.views?.[pose] : undefined;
    const seat = view?.seat ?? fit.seat;
    const width = ((socket.width * H * (view?.scale ?? fit.scale)) / (view?.span ?? fit.span)) * (tweak?.scale ?? 1);
    const height = width * (natural.height / natural.width);
    const side = pose === "side";
    const mirror = slot === "hand" && socket.mirror === true;
    const squash = side && !view ? (fit.sideSquash ?? SIDE_SQUASH[slot] ?? 1) : 1;

    let rotationDeg = (socket.rotation ?? 0) + (view ? (view.tilt ?? 0) : (fit.tilt ?? 0));
    if (slot === "hand") rotationDeg = (fit.tiltIn?.[pose] ?? socket.rotation ?? 0) - (mirror ? -1 : 1) * (fit.axis ?? 0);
    rotationDeg += tweak?.rotation ?? 0;

    // In profile only the near lens of glasses/masks shows, its temple running to the ear.
    const profileLens = side && !view && fit.lens !== undefined;
    const x = (socket.x + (tweak?.x ?? 0)) * body.width + (view?.shift ?? 0) * socket.width * H;
    const y = (socket.y + (tweak?.y ?? 0)) * H + ((view ? view.sink : fit.sink) ?? 0) * socket.width * H;
    const base = {
      slot,
      itemId,
      assetId,
      x,
      y,
      width,
      height,
      originX: profileLens ? fit.lens! : seat.x,
      originY: seat.y,
      scaleX: squash * (mirror ? -1 : 1),
      rotation: rotationDeg * DEG,
      motion: fit.motion,
    };
    const lensCrop: Crop | undefined = profileLens ? { x0: 0, y0: 0, x1: 0.5, y1: 1 } : undefined;

    const behind =
      (fit.behindIn?.includes(pose) ?? false) || (slot === "hand" && socket.behind === true);
    if (fit.wrap !== undefined && !behind) {
      // The whole loop goes behind the body; only its near side comes back in front.
      const near: Crop = pose === "back" ? { x0: 0, y0: 0, x1: 1, y1: fit.wrap } : { x0: 0, y0: fit.wrap, x1: 1, y1: 1 };
      layout.pieces.push({ ...base, layer: "back" }, { ...base, layer: "front", crop: near });
    } else {
      layout.pieces.push({ ...base, layer: behind ? "back" : "front", crop: lensCrop });
    }

    if (slot === "hand" && !behind && socket.fingers) layout.hands.push(socket.fingers);
    if (profileLens && fit.arm !== undefined) {
      const ear = (sockets.eyes as { ear?: { x: number; y: number } } | undefined)?.ear;
      if (ear) {
        layout.arms.push({
          x1: x - fit.lens! * width * squash * 0.85,
          y1: y,
          x2: ear.x * body.width,
          y2: ear.y * H,
          thickness: H * 0.012,
          color: fit.arm,
        });
      }
    }
  }
  return layout;
}
